import { useMemo } from "react";
import { t } from "@/shared/lib/th-translations";
import type {
  PerformanceMetric,
  PerformanceProcessBucket,
  BaselineResponse,
} from "@/shared/types/production";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import ZScoreLegend from "./ZScoreLegend";
import {
  zScoreColor,
  computeZScore,
  hasEnoughBaseline,
  metricValue,
  formatMetricDisplay,
} from "@/frontend/lib/production-stats";

interface Props {
  processes: PerformanceProcessBucket[];
  metric: PerformanceMetric;
  baseline?: BaselineResponse["data"];
  machineSplit: boolean;
  onMachineSplitChange: (v: boolean) => void;
}

function buildTooltip(
  label: string,
  processTitle: string,
  current: number,
  peerStats: { mean: number; stddev: number; count: number } | undefined,
  ownStats: { mean: number; stddev: number; count: number } | undefined,
  ownDeltaPct: number | null,
  metric: PerformanceMetric = "good_units",
): string {
  const lines = [
    `${label} · ${processTitle}`,
    `${t("production.performance.viz.heatmap.tooltipCurrent")}: ${formatMetricDisplay(current, metric)}`,
  ];
  if (peerStats) {
    lines.push(
      `${t("production.performance.viz.heatmap.tooltipPeerBaseline")}: ${formatMetricDisplay(peerStats.mean, metric)}`,
    );
  }
  if (ownStats) {
    lines.push(
      `${t("production.performance.viz.heatmap.tooltipOwnBaseline")}: ${formatMetricDisplay(ownStats.mean, metric)}`,
    );
  }
  if (ownDeltaPct !== null) {
    const sign = ownDeltaPct >= 0 ? "+" : "";
    lines.push(
      `${t("production.performance.viz.heatmap.tooltipDelta")}: ${sign}${ownDeltaPct.toFixed(1)}%`,
    );
  }
  return lines.join("\n");
}

function selectBaseline(
  baseline: BaselineResponse["data"] | undefined,
  metric: PerformanceMetric,
): {
  byProcessMachine: BaselineResponse["data"]["byProcessMachine"];
  byProcessPerson: BaselineResponse["data"]["byProcessPerson"];
  byProcess: BaselineResponse["data"]["byProcess"];
} | undefined {
  if (!baseline) return undefined;
  if (metric === "shots") {
    return {
      byProcessMachine: baseline.byProcessMachineShots,
      byProcessPerson: baseline.byProcessPersonShots,
      byProcess: baseline.byProcessShots,
    };
  }
  if (metric === "paper_length") {
    return {
      byProcessMachine: baseline.byProcessMachinePaperLength,
      byProcessPerson: baseline.byProcessPersonPaperLength,
      byProcess: baseline.byProcessPaperLength,
    };
  }
  if (metric === "paper_area") {
    return {
      byProcessMachine: baseline.byProcessMachinePaperArea,
      byProcessPerson: baseline.byProcessPersonPaperArea,
      byProcess: baseline.byProcessPaperArea,
    };
  }
  return {
    byProcessMachine: baseline.byProcessMachine,
    byProcessPerson: baseline.byProcessPerson,
    byProcess: baseline.byProcess,
  };
}

const Z_BAND_KEYS = [
  "zBandCriticallyLow",
  "zBandLow",
  "zBandNormal",
  "zBandHigh",
  "zBandCriticallyHigh",
] as const;

const Z_BAND_COLORS = [
  "bg-red-500 text-white",
  "bg-red-300 text-gray-900",
  "bg-yellow-200 text-gray-900",
  "bg-green-200 text-gray-900",
  "bg-green-500 text-white",
];

export default function PerformanceHeatmap({
  processes,
  metric,
  baseline,
  machineSplit,
  onMachineSplitChange,
}: Props) {
  // processTitle -> employeeId -> bucket
  const bucketLookup = useMemo(() => {
    const m = new Map<string, Map<string, (typeof processes)[0]["buckets"][0]>>();
    for (const p of processes) {
      const byEmp = new Map<string, (typeof p)["buckets"][0]>();
      for (const b of p.buckets) byEmp.set(b.employeeId, b);
      m.set(p.processTitle, byEmp);
    }
    return m;
  }, [processes]);

  // Unique employees for non-split rows
  const allEmployees = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of processes) {
      for (const b of p.buckets) {
        if (!map.has(b.employeeId)) map.set(b.employeeId, b.employeeName);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [processes]);

  // Employee groups with their machines for split rows
  const employeeGroups = useMemo(() => {
    if (!machineSplit) return [];
    const groups = new Map<
      string,
      { employeeId: string; employeeName: string; machines: string[] }
    >();
    for (const p of processes) {
      for (const b of p.buckets) {
        for (const machine of Object.keys(b.byMachine ?? {})) {
          let g = groups.get(b.employeeId);
          if (!g) {
            g = {
              employeeId: b.employeeId,
              employeeName: b.employeeName,
              machines: [],
            };
            groups.set(b.employeeId, g);
          }
          if (!g.machines.includes(machine)) g.machines.push(machine);
        }
      }
    }
    const arr = Array.from(groups.values());
    arr.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
    for (const g of arr) g.machines.sort();
    return arr;
  }, [processes, machineSplit]);

  const selectedBaseline = selectBaseline(baseline, metric);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>
            {t("production.performance.viz.heatmap.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t("production.performance.viz.heatmap.machineSplitLabel")}:
            </span>
            <div className="flex gap-1">
              <Button
                variant={!machineSplit ? "default" : "outline"}
                size="sm"
                onClick={() => onMachineSplitChange(false)}
              >
                {t("production.performance.viz.heatmap.machineSplitOff")}
              </Button>
              <Button
                variant={machineSplit ? "default" : "outline"}
                size="sm"
                onClick={() => onMachineSplitChange(true)}
              >
                {t("production.performance.viz.heatmap.machineSplitOn")}
              </Button>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground italic">
          {baseline === undefined
            ? t("production.performance.viz.heatmap.baselineLoading")
            : t("production.performance.viz.heatmap.baselineLabel")}
        </p>
      </CardHeader>

      <CardContent>
        <ZScoreLegend
          namespace="production.performance.legend"
          className="mb-3"
        />
        <div className="overflow-auto max-h-[75vh]">
          <table className="w-full border-separate border-spacing-1 text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 bg-background z-20 px-2 py-1 text-left font-medium">
                  {t("production.performance.viz.heatmap.employeeCol")}
                </th>
                {processes.map((p) => (
                  <th
                    key={p.processTitle}
                    className="sticky top-0 bg-background z-10 px-2 py-1 text-xs font-medium text-center whitespace-nowrap"
                    title={p.processTitle}
                  >
                    {p.processTitle}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!machineSplit
                ? allEmployees.map(({ id, name }) => (
                    <tr key={id}>
                      <td className="sticky left-0 bg-background z-10 px-2 py-1 font-medium whitespace-nowrap">
                        {name}
                      </td>
                      {processes.map((p) => {
                        const bucket = bucketLookup.get(p.processTitle)?.get(id);
                        if (!bucket) {
                          return (
                            <td
                              key={p.processTitle}
                              className="px-2 py-1 text-center text-muted-foreground bg-gray-50"
                              title={t("production.performance.viz.heatmap.noData")}
                            >
                              —
                            </td>
                          );
                        }
                        const value = metricValue(bucket, metric);
                        const peerStats = selectedBaseline?.byProcess[p.processTitle];
                        const ownStats =
                          selectedBaseline?.byProcessPerson[`${p.processTitle}||${id}`];
                        if (!peerStats || !hasEnoughBaseline(peerStats.count)) {
                          return (
                            <td
                              key={p.processTitle}
                              className="px-2 py-1 text-center bg-gray-100"
                              title={t(
                                "production.performance.viz.heatmap.insufficientBaseline",
                              )}
                            >
                              {formatMetricDisplay(value, metric)}
                            </td>
                          );
                        }
                        const z = computeZScore(value, peerStats.mean, peerStats.stddev);
                        const ownDeltaPct =
                          ownStats && ownStats.mean > 0
                            ? ((value - ownStats.mean) / ownStats.mean) * 100
                            : null;
                        return (
                          <td
                            key={p.processTitle}
                            className={`px-2 py-1 text-center font-medium rounded ${zScoreColor(z)}`}
                            title={buildTooltip(
                              name,
                              p.processTitle,
                              value,
                              peerStats,
                              ownStats,
                              ownDeltaPct,
                              metric,
                            )}
                          >
                            {formatMetricDisplay(value, metric)}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                : employeeGroups.flatMap((g) => [
                    <tr
                      key={`hdr-${g.employeeId}`}
                      className="bg-muted/40"
                    >
                      <td className="sticky left-0 bg-muted/40 z-10 px-2 py-1 font-semibold whitespace-nowrap">
                        {g.employeeName}
                      </td>
                      {processes.map((p) => (
                        <td
                          key={p.processTitle}
                          className="bg-muted/40"
                        />
                      ))}
                    </tr>,
                    ...g.machines.map((machine) => (
                      <tr key={`${g.employeeId}||${machine}`}>
                        <td className="sticky left-0 bg-background z-10 px-2 py-1 whitespace-nowrap pl-6 text-sm text-muted-foreground">
                          @ {machine}
                        </td>
                        {processes.map((p) => {
                          const bucket = bucketLookup
                            .get(p.processTitle)
                            ?.get(g.employeeId);
                          const machineData = bucket?.byMachine?.[machine];
                          if (!machineData) {
                            return (
                              <td
                                key={p.processTitle}
                                className="px-2 py-1 text-center text-muted-foreground bg-gray-50"
                                title={t(
                                  "production.performance.viz.heatmap.noData",
                                )}
                              >
                                —
                              </td>
                            );
                          }
                          const value =
                            metric === "shots"
                              ? machineData.shots
                              : metric === "good_units"
                              ? machineData.goodUnits
                              : metric === "paper_length"
                              ? machineData.paperLength
                              : metric === "paper_area"
                              ? machineData.paperArea
                              : machineData.events;
                          const peerKey = `${p.processTitle}||${machine}`;
                          const peerStats =
                            selectedBaseline?.byProcessMachine[peerKey];
                          const ownStats =
                            selectedBaseline?.byProcessPerson[
                              `${p.processTitle}||${g.employeeId}`
                            ];
                          if (
                            !peerStats ||
                            !hasEnoughBaseline(peerStats.count)
                          ) {
                            return (
                              <td
                                key={p.processTitle}
                                className="px-2 py-1 text-center bg-gray-100"
                                title={t(
                                  "production.performance.viz.heatmap.insufficientBaseline",
                                )}
                              >
                                {formatMetricDisplay(value, metric)}
                              </td>
                            );
                          }
                          const z = computeZScore(
                            value,
                            peerStats.mean,
                            peerStats.stddev,
                          );
                          const rowLabel = `${g.employeeName} @ ${machine}`;
                          const ownDeltaPct =
                            ownStats && ownStats.mean > 0
                              ? ((value - ownStats.mean) / ownStats.mean) * 100
                              : null;
                          return (
                            <td
                              key={p.processTitle}
                              className={`px-2 py-1 text-center font-medium rounded ${zScoreColor(z)}`}
                              title={buildTooltip(
                                rowLabel,
                                p.processTitle,
                                value,
                                peerStats,
                                ownStats,
                                ownDeltaPct,
                                metric,
                              )}
                            >
                              {formatMetricDisplay(value, metric)}
                            </td>
                          );
                        })}
                      </tr>
                    )),
                  ])}
            </tbody>
          </table>
        </div>

        {/* Z-Score legend */}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{t("production.performance.viz.heatmap.zLegendLabel")}:</span>
          {Z_BAND_KEYS.map((key, i) => (
            <span key={key} className={`px-2 py-0.5 rounded ${Z_BAND_COLORS[i]}`}>
              {t(`production.performance.viz.heatmap.${key}`)}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
